import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, Mail } from 'lucide-react';
import type { EmailTemplateToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { serverMessage } from '~/lib/api/server-error';
import { useEmailTemplatesList, useEmailTemplateSites, useEmailTemplateMutations } from '~/hooks/use-email-templates';

/** Email templates CRUD surface — Code/From/Subject/Type/Site/Visible/Sort grid; edits open the full-page editor. */
export function EmailTemplatesGridView() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { data, isLoading, isError, refetch } = useEmailTemplatesList();
	const { data: sites = [] } = useEmailTemplateSites();
	const { remove } = useEmailTemplateMutations();

	const [deleteRow, setDeleteRow] = useState<EmailTemplateToken | null>(null);

	const siteMap = useMemo(() => new Map(sites.map(s => [s.id, s.nm])), [sites]);
	const typeLabel = (ty?: number) => (ty === 1 ? t('system.emailTemplates.engineHandlebars') : t('system.emailTemplates.engineBizweb'));

	const columns = useMemo<SystemGridColumn<EmailTemplateToken>[]>(
		() => [
			{
				key: 'code',
				header: t('system.emailTemplates.columns.code'),
				width: '200px',
				sortValue: e => e.cd,
				exportValue: e => e.cd,
				render: e => <span className="truncate font-mono text-xs font-medium text-foreground">{e.cd}</span>
			},
			{
				key: 'subject',
				header: t('system.emailTemplates.columns.subject'),
				width: 'minmax(0,1fr)',
				sortValue: e => e.sub,
				exportValue: e => e.sub,
				render: e => (
					<span className="truncate text-sm font-medium text-foreground" title={e.sub}>
						{e.sub}
					</span>
				)
			},
			{
				key: 'fromEmail',
				header: t('system.emailTemplates.columns.fromEmail'),
				width: '200px',
				sortValue: e => e.feml,
				exportValue: e => e.feml,
				render: e => <span className="truncate text-sm text-foreground-muted">{e.feml}</span>
			},
			{
				key: 'type',
				header: t('system.emailTemplates.columns.type'),
				width: '110px',
				sortValue: e => e.ttype ?? 0,
				exportValue: e => typeLabel(e.ttype),
				render: e => <span className="text-sm text-foreground-muted">{typeLabel(e.ttype)}</span>
			},
			{
				key: 'site',
				header: t('system.emailTemplates.columns.site'),
				width: '120px',
				sortValue: e => (e.wsid == null ? '' : (siteMap.get(e.wsid) ?? '')),
				exportValue: e => (e.wsid == null ? t('system.emailTemplates.allSites') : (siteMap.get(e.wsid) ?? '')),
				render: e =>
					e.wsid == null ? (
						<span className="text-sm text-foreground-subtle">{t('system.emailTemplates.allSites')}</span>
					) : (
						<span className="truncate text-sm text-foreground-muted">{siteMap.get(e.wsid) ?? `#${e.wsid}`}</span>
					)
			},
			{
				key: 'visible',
				header: t('system.emailTemplates.columns.visible'),
				width: '80px',
				align: 'center',
				sortValue: e => (e.iv ? 1 : 0),
				exportValue: e => (e.iv ? t('common.yes') : t('common.no')),
				render: e => (e.iv ? <Check size={16} className="text-success-fg" /> : <span className="text-foreground-disabled">—</span>)
			},
			{
				key: 'sort',
				header: t('system.emailTemplates.columns.sort'),
				width: '70px',
				align: 'right',
				sortValue: e => e.si ?? 0,
				exportValue: e => e.si ?? 0,
				render: e => <span className="text-sm tabular-nums text-foreground-muted">{e.si}</span>
			},
			...(
				[
					['fromName', t('system.fields.fromName'), (e: EmailTemplateToken) => e.fnm],
					['toNames', t('system.emailTemplates.toNames'), (e: EmailTemplateToken) => e.tnms],
					['toEmails', t('system.emailTemplates.toEmails'), (e: EmailTemplateToken) => e.temls],
					['ccNames', t('system.emailTemplates.ccNames'), (e: EmailTemplateToken) => e.cnms],
					['ccEmails', t('system.emailTemplates.ccEmails'), (e: EmailTemplateToken) => e.cemls],
					['bccNames', t('system.emailTemplates.bccNames'), (e: EmailTemplateToken) => e.bcnms],
					['bccEmails', t('system.emailTemplates.bccEmails'), (e: EmailTemplateToken) => e.bcemls],
					['body', t('system.fields.body'), (e: EmailTemplateToken) => e.bd]
				] as const
			).map(
				([key, header, get]): SystemGridColumn<EmailTemplateToken> => ({
					key,
					header,
					width: '200px',
					defaultVisible: false,
					sortValue: e => get(e) ?? '',
					exportValue: e => get(e) ?? '',
					render: e => (
						<span className="truncate text-xs text-foreground-muted" title={get(e) ?? ''}>
							{get(e) || '—'}
						</span>
					)
				})
			)
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[t, siteMap]
	);

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: err => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<SystemGrid<EmailTemplateToken>
				gridId="email-templates"
				title={t('system.emailTemplates.title')}
				rows={data ?? []}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={e => e.id ?? e.cd}
				columns={columns}
				searchPredicate={(e, q) => `${e.cd ?? ''} ${e.sub ?? ''} ${e.feml ?? ''}`.toLowerCase().includes(q)}
				defaultSort={{ key: 'code', dir: 'asc' }}
				emptyIcon={<Mail size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				addLabel={t('system.emailTemplates.add')}
				onAdd={() => navigate('/system/email-templates/create')}
				onEdit={e => navigate(`/system/email-templates/${e.id}`)}
				onDelete={setDeleteRow}
				exportFilename="email-templates"
			/>

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('system.emailTemplates.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('system.emailTemplates.deleteBody', { name: deleteRow?.cd ?? '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
